import { FC, useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { scroller } from 'react-scroll';
import PageTopButton from '@common/pageTopButton';
import Experience from '@components/profile/userInfoItems/experience';
import MilitaryPreference from '@components/profile/userInfoItems/militaryPreference';
import BasicInfo from '@components/profile/userInfoItems/basicInfo';
import Prefer from '@components/profile/userInfoItems/prefer';
import Edu from '@components/profile/userInfoItems/edu';
import Career from '@components/profile/userInfoItems/career';
import Project from '@components/profile/userInfoItems/project';
import Skill from '@components/profile/userInfoItems/skill';
import AttachedFile from '@components/profile/userInfoItems/attachedFile';
import ProfileSnb from '@components/profile/profileSnb';
import ProfileOverlay from '@components/profile/profileOverlay';
import ProfileModal, { ProfileModalType } from '@components/profile/modals';
import { useUserInfo } from '@repository/auth/useUserInfo';
import useProfileBasic from '@repository/profiles/useProfileBasic';
import useProfileConditions from '@repository/profiles/useProfileConditions';
import useProfileEducation from '@repository/profiles/useProfileEducation';
import Recommendation from '@components/profile/userInfoItems/recommendation';
import { useGetMatchConfigStatus } from '@repository/match';
import Prize from '@components/profile/userInfoItems/prize';
import NoticeAutoSave from './userInfoItems/noticeAutoSave';
import { ProfileInfo, ProfileInfoType, ProfileOverlayType } from '@domain/constant/profile/ProfileOverlayType';
import UrlUtil from '@utils/urlUtil';
import classnames from 'classnames/bind';
import styles from './profilePage.module.scss';

const cx = classnames.bind(styles);

const WriteNotice = ({ onClick, onClose }: { onClick: () => void; onClose: () => void }) => {
  return (
    <div className={cx('writeNoticeArea')}>
      <a className={cx('btnWriteNotice', 'pc_profile_floating_btn_0831')} role="button" onClick={onClick}>
        필수정보 3가지만 작성하고 기업에게 제안을 받아보세요.👍
      </a>
      <button className={cx('closeBtn')} type={'button'} onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.0118 5.13853C20.3294 4.82088 20.3294 4.30588 20.0118 3.98823C19.6941 3.67059 19.1791 3.67059 18.8615 3.98823L12 10.8497L5.13853 3.98823C4.82088 3.67059 4.30588 3.67059 3.98823 3.98823C3.67059 4.30588 3.67059 4.82088 3.98823 5.13853L10.8497 12L3.98823 18.8615C3.67059 19.1791 3.67059 19.6941 3.98823 20.0118C4.30588 20.3294 4.82088 20.3294 5.13853 20.0118L12 13.1503L18.8615 20.0118C19.1791 20.3294 19.6941 20.3294 20.0118 20.0118C20.3294 19.6941 20.3294 19.1791 20.0118 18.8615L13.1503 12L20.0118 5.13853Z"
            fill="#EFFDF3"
          />
        </svg>
      </button>
    </div>
  );
};

const Profile: FC = () => {
  const router = useRouter();
  const { data: userInfo, isLoading } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const [modalState, setModalState] = useState<ProfileModalType>(null);

  const [overlayState, setOverlayState] = useState<ProfileOverlayType>(null);

  const { data: profileBasicData } = useProfileBasic(isLogin);
  const { data: profileConditionsData } = useProfileConditions(isLogin);
  const { data: profileEducationData } = useProfileEducation(isLogin);
  const { data: matchConfigStatus } = useGetMatchConfigStatus(isLogin);

  const [isInitWriteNotice, setIsInitWriteNotice] = useState(false);
  const [isCloseWriteNotice, setIsCloseWriteNotice] = useState(false);
  const writeNoticeVisible =
    isInitWriteNotice &&
    ![profileBasicData, profileConditionsData, profileEducationData].every((item) => item?.allSaveYn);

  const handleClickWriteNotice = () => {
    if (!profileBasicData?.allSaveYn) {
      onOpenOverlay(ProfileInfo.BASIC_INFO);
      return;
    }

    if (!profileConditionsData?.allSaveYn) {
      onOpenOverlay(ProfileInfo.PREFER);
      return;
    }

    if (!profileEducationData?.allSaveYn) {
      onOpenOverlay(ProfileInfo.EDU);
      return;
    }
  };

  const onOpenOverlay = useCallback(
    (type: ProfileOverlayType) => {
      if (!isLogin) {
        setModalState('LOGIN');
        return;
      }
      setOverlayState(type);
    },
    [isLogin],
  );

  const openNHISModal = useCallback(() => {
    setModalState('NHIS');
  }, []);

  const openProfileModal = useCallback(() => {
    setModalState('RESUME_PROFILE');
  }, []);

  useEffect(() => {
    if (profileBasicData && profileConditionsData && profileEducationData) setIsInitWriteNotice(true);
  }, [profileBasicData, profileConditionsData, profileEducationData, matchConfigStatus]);

  useEffect(() => {
    if (isLoading) return;
    const name = router.query.name as ProfileInfoType;
    if (!name || !Object.keys(ProfileInfo).includes(name)) return;

    scroller.scrollTo(name, { smooth: true });
    onOpenOverlay(name);
    UrlUtil.removeQueryStringAndReplace('name');
  }, [router.query, onOpenOverlay, isLoading]);

  return (
    <>
      <div className={cx('profilePageArea')}>
        <div className={cx('profilePageInner')}>
          <div className={cx('userInfoArea')}>
            {/* 배너 / SNB 영역 */}
            <div className={cx('profileBannerArea')}>
              {/* SNB 영역 */}
              <ProfileSnb
                handleClickCareerBanner={() => {
                  onOpenOverlay('CAREER');
                  openNHISModal();
                }}
                handleClickResumeProfileBanner={openProfileModal}
              />
            </div>

            {/* 사용자 정보 view 영역 */}
            <div className={cx('userInfoInner')}>
              {/*면접 제안*/}
              <Recommendation />
              {/* 기본 정보 */}
              <BasicInfo onOpenOverlay={() => onOpenOverlay('BASIC_INFO')} />
              {/* autoSave노티스 */}
              <NoticeAutoSave />
              {/* 선호 정보 */}
              <Prefer onOpenOverlay={() => onOpenOverlay('PREFER')} />
              {/* 학력 정보 */}
              <Edu onOpenOverlay={() => onOpenOverlay('EDU')} />
              {/* 경력 정보 */}
              <Career onOpenOverlay={() => onOpenOverlay('CAREER')} />
              {/* 프로젝트 */}
              <Project onOpenOverlay={() => onOpenOverlay('PROJECT')} />
              {/* 지식 · 기술 */}
              <Skill onOpenOverlay={() => onOpenOverlay('SKILL')} />
              {/* 경험 정보 */}
              <Experience onOpenOverlay={() => onOpenOverlay('EXPERIENCE')} />
              {/* 수상 내역 */}
              <Prize onOpenOverlay={() => onOpenOverlay('PRIZE')} />
              {/* 병역 · 취업 우대 */}
              <MilitaryPreference onOpenOverlay={() => onOpenOverlay('MILITARY_PREFERENCE')} />
              {/* 첨부 자료 */}
              <AttachedFile onOpenOverlay={() => onOpenOverlay('ATTACHED_FILE')} />
            </div>
          </div>
        </div>

        {/* 프로필 작성 배너 (약관 동의 시 프로필 이름, 후대번호 자동 생성되도록 되어 있어 동의 여부로 판단 가능) */}
        {writeNoticeVisible && !isCloseWriteNotice && (
          <WriteNotice
            onClick={handleClickWriteNotice}
            onClose={() => {
              setIsCloseWriteNotice(true);
            }}
          />
        )}

        {/* Top 버튼 영역 */}
        <PageTopButton />

        {/* 입력폼(overlay) 영역 */}
        <ProfileOverlay overlayState={overlayState} setOverlayState={setOverlayState} openNHISModal={openNHISModal} />

        {/* Modal (ex. 로그인, 약관 등) 영역 */}
        <ProfileModal modalState={modalState} setModalState={setModalState} />
      </div>
    </>
  );
};

export default Profile;
